import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import { consultNav } from "../i18n/consult";

const Logo = () => (
  <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
    <img src="/logo.svg" alt="EMAN TEAM Dynamics" className="w-9 h-9" />
    <span className="leading-none">
      <span className="block font-display font-extrabold tracking-tight text-slate-50 text-base">EMAN TEAM</span>
      <span className="block font-mono text-[9px] tracking-[0.42em] text-sovereign mt-1">DYNAMICS</span>
    </span>
  </Link>
);

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: "/partners", label: t.nav.partners, testid: "nav-partners" },
    { to: "/ibm-solutions", label: t.nav.ibm, testid: "nav-ibm" },
    { to: "/konect-systems", label: t.nav.konect, testid: "nav-konect" },
    { to: "/3eeco", label: t.nav.eeco, testid: "nav-eeco" },
    { to: "/consultanta-audit", label: consultNav[lang], testid: "nav-consult" },
    { to: "/critical-infrastructure", label: t.nav.infra, testid: "nav-infra" },
    { to: "/datacenter-ai", label: t.nav.dc, testid: "nav-dc" },
  ];

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "bg-obsidian/85 backdrop-blur-xl border-b border-sovereign/10" : "bg-transparent"}`}
        data-testid="navbar"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
          <Logo />
          <nav className="hidden xl:flex items-center gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `font-mono text-[10px] tracking-[0.1em] uppercase whitespace-nowrap transition-colors duration-300 ${isActive ? "text-sovereign" : "text-slate-400 hover:text-slate-100"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3 lg:gap-4 shrink-0">
            <div className="flex items-center border border-slate-700" data-testid="language-toggle">
              {["ro", "en"].map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  data-testid={`language-toggle-${l}`}
                  className={`px-3 py-1.5 font-mono text-[11px] tracking-[0.2em] uppercase transition-colors duration-300 ${lang === l ? "bg-sovereign text-obsidian font-bold" : "text-slate-400 hover:text-slate-100"}`}
                >
                  {l}
                </button>
              ))}
            </div>
            <Link
              to="/contact"
              data-testid="nav-cta"
              className="hidden xl:inline-flex items-center gap-2 bg-sovereign text-obsidian font-semibold px-5 py-2.5 text-xs tracking-[0.12em] uppercase whitespace-nowrap hover:shadow-[0_0_30px_rgba(0,240,255,0.35)] transition-shadow duration-300"
            >
              {t.nav.cta}
            </Link>
            <button
              className="xl:hidden flex flex-col justify-center gap-1.5 w-9 h-9 items-center border border-slate-700"
              onClick={() => setOpen(!open)}
              data-testid="nav-mobile-toggle"
              aria-label={open ? t.nav.close : t.nav.menu}
            >
              <span className={`block w-5 h-px bg-slate-200 transition-transform duration-300 ${open ? "rotate-45 translate-y-[3.5px]" : ""}`} />
              <span className={`block w-5 h-px bg-slate-200 transition-transform duration-300 ${open ? "-rotate-45 -translate-y-[3px]" : ""}`} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-obsidian/97 backdrop-blur-2xl pt-28 px-6 overflow-y-auto"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col gap-2 pb-12">
              {[{ to: "/", label: t.nav.home, testid: "nav-mobile-home" }, ...links, { to: "/contact", label: t.nav.cta, testid: "nav-mobile-contact" }].map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4 }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={l.testid}
                    className={({ isActive }) =>
                      `block font-display text-2xl font-bold uppercase py-3 border-b border-slate-800 ${isActive ? "text-sovereign" : "text-slate-200"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
